<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { IStatsContext } from '@/interfaces/IStatsContext';
  import { PermissionType } from '@/enums/PermissionType';
  import { useAuth } from '@/composition/use-auth';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import {
    getBusinessDaySettingsPath,
    getBusinessFootnoteParams,
  } from '@/domain/stats/business-day';

  const props = defineProps<{
    /** Business-day context; parents render the note only when its start hour is > 0. */
    context: IStatsContext;
    /** Branch the context belongs to (the pencil edits this branch). */
    branchId: number;
  }>();

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;
  const restaurantStore = useRestaurantStore();
  const auth = useAuth();

  /** "Día comercial 21/08 = 21/08 06:00 → 22/08 05:59" */
  const text = computed(() => {
    const params = getBusinessFootnoteParams(props.context);
    return params
      ? String(proxy.$t('v2Stats.range.businessFootnote', params))
      : '';
  });

  /** Settings page holding the start hour, when this user can change it. */
  const editPath = computed(() =>
    getBusinessDaySettingsPath({
      restaurantSlug: restaurantStore.restaurant?.stringId ?? '',
      branchId: props.branchId,
      activeBranchCount: restaurantStore.visibleBranches?.length ?? 0,
      effectiveTier: restaurantStore.effectiveTier,
      canEditStoreSettings: auth.hasPermission(PermissionType.StoreSettings),
      canEditBranchSettings: auth.hasPermission(PermissionType.BranchSettings),
    })
  );

  const editLabel = computed(() =>
    String(proxy.$t('branchSettings.businessDayStartHour'))
  );
</script>

<template>
  <div class="v2-stats-business-day-note">
    <span class="note-text">{{ text }}</span>
    <router-link
      v-if="editPath"
      :to="editPath"
      class="note-edit"
      :title="editLabel"
      :aria-label="editLabel"
    >
      <PosUiIcon name="pencil-outline" :size="16" />
    </router-link>
  </div>
</template>

<style scoped lang="scss">
  .v2-stats-business-day-note {
    @apply tw-flex tw-items-center tw-text-xs tw-text-neutral-500;

    .note-text {
      @apply tw-min-w-0;
    }

    // Just the pencil, inside a 44px hit area: the negative vertical margin
    // keeps the row at 20px (the box overhangs 12px above and below; parents
    // reserve that space) and the negative left margin keeps the glyph close
    // to the text. Hover and keyboard focus only darken the icon.
    .note-edit {
      @apply -tw-my-3 -tw-ml-2 tw-inline-flex tw-h-11 tw-w-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-text-neutral-500 tw-no-underline tw-transition-colors;

      &:hover {
        @apply tw-text-neutral-900;
      }

      // Separate rule: older POS Chrome drops a whole selector list that
      // contains :focus-visible (hover must keep working there).
      &:focus-visible {
        @apply tw-text-neutral-900 tw-outline-none;
      }
    }
  }
</style>
